import { useMemo } from 'react';

import { useDecisionMakerProfilePageData } from '#src/app/pages/decisionMaker/profile/pageContext';

type Data = {
	dmId: string;
	slug: string;
	slugCopyable: boolean;
	type: string;
	publiclyVisible: string;
	verified: string;
	location: string;
	urls: {
		dmAdmin: string;
		edit: string;
		adminAccounts: string;
	};
	responsiveness:
		| {
				score: string;
				respondedRelevant: string | undefined;
				visible: string;
		  }
		| undefined;
	enrichedData: {
		present: string;
		endorsementCount: string | undefined;
		voterGuidePickCount: string | undefined;
	};
};

type Result = ModelHookResult<Data>;

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

function buildLocation(
	country: { countryCode: string } | null | undefined,
	state: { displayName: string } | null | undefined,
	district: string | null | undefined,
): string {
	const parts: string[] = [];
	if (country) parts.push(country.countryCode);
	if (state) parts.push(state.displayName);
	if (district) parts.push(/\d/.test(district) ? `District ${district}` : district);
	return parts.length > 0 ? parts.join(', ') : '—';
}

export function useDmProfileStaffDrawer(): Result {
	const {
		data: { id, slug, type, publiclyVisible, verifiedAt, country, state, district, responsivenessData, enrichedData },
	} = useDecisionMakerProfilePageData();

	const urls = useMemo(() => {
		const slugOrId = encodeURIComponent(slug || id);
		return {
			dmAdmin: `/admin/decision-makers/${slugOrId}`,
			edit: `/admin/decision-makers/${slugOrId}/edit`,
			adminAccounts: `/admin/decision-makers/${slugOrId}/admin_accounts`,
		};
	}, [id, slug]);

	// responsivenessScore (and its sibling counts) are null until the DM has been scored;
	// treat that as "no responsiveness data" and omit the whole section.
	const hasResponsivenessData = responsivenessData.responsivenessScore !== null;

	// The counts are independently nullable from the score and from each other; a non-null
	// score doesn't guarantee both counts exist. Only treat them as a measured ratio when both
	// are non-null (matching the useDmScoreData convention), otherwise omit the info line
	// rather than presenting a missing count as a false zero.
	const hasCountValues =
		responsivenessData.respondedToPetitions !== null && responsivenessData.totalRelevantPetitions !== null;

	return {
		data: {
			dmId: id,
			slug: slug ?? '—',
			slugCopyable: Boolean(slug),
			type,
			publiclyVisible: yesNo(publiclyVisible),
			verified: yesNo(Boolean(verifiedAt)),
			location: buildLocation(country, state, district),
			urls,
			responsiveness: hasResponsivenessData
				? {
						score: String(responsivenessData.responsivenessScore),
						respondedRelevant: hasCountValues
							? `${responsivenessData.respondedToPetitions} / ${responsivenessData.totalRelevantPetitions}`
							: undefined,
						visible: yesNo(responsivenessData.isVisible),
					}
				: undefined,
			enrichedData: {
				present: yesNo(Boolean(enrichedData)),
				endorsementCount: enrichedData ? String((enrichedData.endorsements ?? []).length) : undefined,
				voterGuidePickCount: enrichedData ? String((enrichedData.voterGuidePicks ?? []).length) : undefined,
			},
		},
	};
}
